import axios from 'axios';

import type { ApiResponse, MediaProcessingJob } from '@kangaroo/shared';

import { api } from './api';
import type { GoProUploadObjectKey } from './goproUploadState';

export const GOPRO_UPLOAD_CHUNK_SIZE_BYTES = 8 * 1024 * 1024;
export const GOPRO_UPLOAD_RETRY_COUNT = 6;
export const GOPRO_UPLOAD_CHUNK_TIMEOUT_MS = 120_000;
export const GOPRO_UPLOAD_STALL_TIMEOUT_MS = 45_000;

export type GoProUploadObjectStatus = {
  objectPath: string;
  contentType: string;
  sizeBytes: number;
  uploadedBytes: number;
  complete: boolean;
};

export type GoProUploadSessionResponse = {
  uploadSessionId: string;
  bucket: string;
  sourceObjectPath: string;
  processedBase: string;
  objects: Partial<Record<GoProUploadObjectKey, GoProUploadObjectStatus>>;
};

export type GoProUploadProgress = {
  objectKey: GoProUploadObjectKey;
  uploadedBytes: number;
  totalBytes: number;
  percent: number;
  message: string;
};

export type GoProUploadSessionListItem = {
  uploadSessionId: string;
  requestedByUserId: string;
  uploadedBy?: {
    userId: string;
    name: string;
    avatarUrl: string | null;
  } | null;
  originalName: string;
  status: string;
  jobId: string | null;
  sessionId?: string | null;
  recordingId?: string | null;
  mediaId?: string | null;
  cameraSourceId?: string | null;
  replay?: {
    status: 'not_started' | 'submitting' | 'pending' | 'dispatched' | 'processing' | 'finalizing' | 'unavailable' | 'ready' | 'failed';
    progressPercent?: number | null;
    integrityVerified?: boolean;
    retryEligible?: boolean;
    attemptId?: string | null;
  } | null;
  startedAtIso?: string | null;
  endedAtIso?: string | null;
  videoDurationSec?: number | null;
  footageTimingSource?: string | null;
  detectedCreatedAtIso?: string | null;
  detectedCreatedAtSource?: string | null;
  fileLastModifiedIso?: string | null;
  footageStartConfirmedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  videoUrl?: string | null;
  replayUrl?: string | null;
  telemetrySummaryUrl?: string | null;
  telemetryUrl?: string | null;
  gpsUrl?: string | null;
  objects: Partial<Record<GoProUploadObjectKey, Pick<GoProUploadObjectStatus, 'sizeBytes' | 'uploadedBytes' | 'complete'>>>;
};

export type GoProUploadSessionPage = {
  items: GoProUploadSessionListItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type GoProUploadSessionPageOptions = {
  cursor?: string | null;
  pageSize?: number;
};

export function isSessionSelectorFootageCacheKey(key: unknown, sessionId: string) {
  return Array.isArray(key)
    && key[0] === 'session-selector-footage'
    && key[1] === sessionId;
}

export type ResumableUploadControls = {
  isPaused: () => boolean;
  waitIfPaused: () => Promise<void>;
  signal?: AbortSignal;
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function parseExpectedUploadOffset(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }

  const payload = error.response.data as { expectedOffset?: unknown; message?: unknown } | undefined;
  if (typeof payload?.expectedOffset === 'number' && Number.isFinite(payload.expectedOffset)) {
    return payload.expectedOffset;
  }

  const message = typeof payload?.message === 'string'
    ? payload.message
    : error.message;
  const match = /Upload must continue at byte (\d+)/.exec(message);
  return match ? Number.parseInt(match[1]!, 10) : null;
}

function isNonRetryableUploadError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

async function withUploadRetry<T>(
  operation: () => Promise<T>,
  onRetry: (attempt: number, error: unknown) => void,
) {
  let lastError: unknown;
  for (let attempt = 0; attempt <= GOPRO_UPLOAD_RETRY_COUNT; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (isNonRetryableUploadError(error)) {
        break;
      }
      if (attempt >= GOPRO_UPLOAD_RETRY_COUNT) {
        break;
      }

      onRetry(attempt + 1, error);
      const delayMs = Math.min(15_000, 750 * 2 ** attempt);
      await sleep(delayMs);
    }
  }

  throw lastError;
}

export async function createGoProUploadSession(params: {
  originalName: string;
  videoSizeBytes: number;
  telemetrySummarySizeBytes?: number;
  telemetrySizeBytes?: number;
  sessionId?: string;
  metadata?: Record<string, string | number | boolean | null>;
}) {
  const response = await api.post<ApiResponse<GoProUploadSessionResponse>>(
    '/media-processing/uploads/gopro/sessions',
    params,
  );
  return response.data.data;
}

export async function fetchGoProUploadSession(uploadSessionId: string) {
  const response = await api.get<ApiResponse<{
    uploadSessionId: string;
    status: string;
    originalName: string;
    sourceObjectPath: string;
    processedBase: string;
    jobId: string | null;
    objects: Partial<Record<GoProUploadObjectKey, GoProUploadObjectStatus>>;
  }>>(`/media-processing/uploads/gopro/sessions/${uploadSessionId}`);
  return response.data.data;
}

export async function completeGoProUploadSession(uploadSessionId: string) {
  const response = await api.post<ApiResponse<{
    id: string;
    state: 'QUEUED';
    uploadSessionId: string;
    source: {
      bucket: string;
      objectPath: string;
      originalName: string;
      sizeBytes: number;
      contentType: string;
    };
    job: MediaProcessingJob;
  }>>(`/media-processing/uploads/gopro/sessions/${uploadSessionId}/complete`);
  return response.data.data;
}

async function uploadChunk(
  uploadSessionId: string,
  objectKey: GoProUploadObjectKey,
  chunk: Blob,
  start: number,
  end: number,
  total: number,
  onProgress?: (loaded: number, totalBytes: number) => void,
  onCommit?: () => void,
) {
  const controller = new AbortController();
  let commitNotified = false;
  let timeout = window.setTimeout(() => {
    controller.abort();
  }, GOPRO_UPLOAD_STALL_TIMEOUT_MS);
  const resetTimeout = () => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      controller.abort();
    }, GOPRO_UPLOAD_STALL_TIMEOUT_MS);
  };

  await api.put(
    `/media-processing/uploads/gopro/sessions/${uploadSessionId}/objects/${objectKey}`,
    chunk,
    {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Range': `bytes ${String(start)}-${String(end)}/${String(total)}`,
      },
      timeout: GOPRO_UPLOAD_CHUNK_TIMEOUT_MS,
      onUploadProgress: (event) => {
        if (!onProgress) {
          return;
        }

        const chunkUploaded = event.loaded ?? chunk.size;
        onProgress(start + chunkUploaded, total);
        resetTimeout();
        if (!commitNotified && chunkUploaded >= chunk.size) {
          commitNotified = true;
          onCommit?.();
        }
      },
    },
  ).finally(() => {
    window.clearTimeout(timeout);
  });
}

export async function uploadResumableObject(params: {
  uploadSessionId: string;
  objectKey: GoProUploadObjectKey;
  blob: Blob;
  startByte?: number;
  controls?: ResumableUploadControls;
  onProgress?: (progress: GoProUploadProgress) => void;
  onChunkComplete?: (uploadedBytes: number) => void | Promise<void>;
}) {
  const totalBytes = params.blob.size;
  let offset = params.startByte ?? 0;

  while (offset < totalBytes) {
    if (params.controls?.signal?.aborted) {
      throw new DOMException('Upload aborted', 'AbortError');
    }

    await params.controls?.waitIfPaused();

    const end = Math.min(offset + GOPRO_UPLOAD_CHUNK_SIZE_BYTES - 1, totalBytes - 1);
    const chunk = params.blob.slice(offset, end + 1);
    const chunkStart = offset;

    try {
      await withUploadRetry(
        () => uploadChunk(
          params.uploadSessionId,
          params.objectKey,
          chunk,
          chunkStart,
          end,
          totalBytes,
          (loaded, total) => {
            params.onProgress?.({
              objectKey: params.objectKey,
              uploadedBytes: loaded,
              totalBytes: total,
              percent: Math.min(100, Math.floor((loaded / total) * 100)),
              message: `Uploading ${params.objectKey}: ${Math.floor((loaded / total) * 100)}%`,
            });
          },
          () => {
            params.onProgress?.({
              objectKey: params.objectKey,
              uploadedBytes: end + 1,
              totalBytes,
              percent: Math.min(100, Math.floor(((end + 1) / totalBytes) * 100)),
              message: `Committing ${params.objectKey} chunk to cloud storage…`,
            });
          },
        ),
        (attempt, error) => {
          params.onProgress?.({
            objectKey: params.objectKey,
            uploadedBytes: chunkStart,
            totalBytes,
            percent: Math.min(100, Math.floor((chunkStart / totalBytes) * 100)),
            message: axios.isCancel(error) || (axios.isAxiosError(error) && !error.response)
              ? `Chunk stalled. Retrying (attempt ${String(attempt)}/${String(GOPRO_UPLOAD_RETRY_COUNT + 1)})…`
              : `Retrying chunk (attempt ${String(attempt)}/${String(GOPRO_UPLOAD_RETRY_COUNT + 1)})…`,
          });
        },
      );
    } catch (error) {
      const expectedOffset = parseExpectedUploadOffset(error);
      if (expectedOffset !== null) {
        offset = expectedOffset;
        await params.onChunkComplete?.(offset);
        params.onProgress?.({
          objectKey: params.objectKey,
          uploadedBytes: offset,
          totalBytes,
          percent: Math.min(100, Math.floor((offset / totalBytes) * 100)),
          message: `Resuming ${params.objectKey} from ${Math.floor((offset / totalBytes) * 100)}%`,
        });
        continue;
      }

      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const session = await fetchGoProUploadSession(params.uploadSessionId);
        const remoteOffset = session.objects[params.objectKey]?.uploadedBytes ?? offset;
        if (remoteOffset > offset) {
          offset = remoteOffset;
          await params.onChunkComplete?.(offset);
          params.onProgress?.({
            objectKey: params.objectKey,
            uploadedBytes: offset,
            totalBytes,
            percent: Math.min(100, Math.floor((offset / totalBytes) * 100)),
            message: `Resuming ${params.objectKey} from ${Math.floor((offset / totalBytes) * 100)}%`,
          });
          continue;
        }
      }

      throw error;
    }

    offset = end + 1;
    await params.onChunkComplete?.(offset);
    params.onProgress?.({
      objectKey: params.objectKey,
      uploadedBytes: offset,
      totalBytes,
      percent: Math.min(100, Math.floor((offset / totalBytes) * 100)),
      message: `Uploading ${params.objectKey}: ${Math.floor((offset / totalBytes) * 100)}%`,
    });
  }
}

export async function listOpenGoProUploadSessions() {
  const page = await listGoProUploadSessionPage({ status: 'open' });
  return page.items;
}

async function listGoProUploadSessionPage(params: {
  status: 'open' | 'completed' | 'all';
  sessionId?: string;
  cursor?: string | null;
  pageSize?: number;
}) {
  const response = await api.get<ApiResponse<GoProUploadSessionPage>>(
    '/media-processing/uploads/gopro/sessions',
    {
      params: {
        status: params.status,
        sessionId: params.sessionId,
        cursor: params.cursor ?? undefined,
        pageSize: params.pageSize,
      },
    },
  );
  const page = response.data.data;
  if (!Array.isArray(page.items) || typeof page.hasMore !== 'boolean') {
    throw new Error('GoPro upload history returned invalid pagination metadata.');
  }
  if (page.hasMore !== Boolean(page.nextCursor)) {
    throw new Error('GoPro upload history changed while loading. Retry to refresh the complete list.');
  }
  return page;
}

export function listCompletedGoProUploadSessionsPage(options: GoProUploadSessionPageOptions = {}) {
  return listGoProUploadSessionPage({ status: 'completed', ...options });
}

export function listSessionFootageUploadsPage(
  sessionId: string,
  options: GoProUploadSessionPageOptions = {},
) {
  return listGoProUploadSessionPage({ status: 'completed', sessionId, ...options });
}

async function collectGoProUploadSessionPages(
  loadPage: (cursor: string | null) => Promise<GoProUploadSessionPage>,
) {
  const items: GoProUploadSessionListItem[] = [];
  const seenIds = new Set<string>();
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  do {
    const page = await loadPage(cursor);
    for (const item of page.items) {
      if (seenIds.has(item.uploadSessionId)) {
        throw new Error('GoPro upload history changed while loading. Retry to refresh the complete list.');
      }
      seenIds.add(item.uploadSessionId);
      items.push(item);
    }

    cursor = page.nextCursor;
    if (cursor && seenCursors.has(cursor)) {
      throw new Error('GoPro upload history returned a repeated cursor. Retry to refresh the complete list.');
    }
    if (cursor) {
      seenCursors.add(cursor);
    }
  } while (cursor);

  return items;
}

export function listCompletedGoProUploadSessions() {
  return collectGoProUploadSessionPages((cursor) => listCompletedGoProUploadSessionsPage({ cursor }));
}

export function listSessionFootageUploads(sessionId: string) {
  return collectGoProUploadSessionPages((cursor) => listSessionFootageUploadsPage(sessionId, { cursor }));
}

export async function updateGoProUploadSessionMetadata(
  uploadSessionId: string,
  metadata: Record<string, string | number | boolean | null>,
) {
  const response = await api.patch<ApiResponse<{
    uploadSessionId: string;
    status: string;
    originalName: string;
    sessionId: string | null;
    metadata: Record<string, string | number | boolean | null>;
    updatedAt: string;
  }>>(`/media-processing/uploads/gopro/sessions/${uploadSessionId}`, { metadata });
  return response.data.data;
}

export async function deleteGoProUploadSession(uploadSessionId: string) {
  await api.delete(`/media-processing/uploads/gopro/sessions/${uploadSessionId}`);
}

export async function retryGoProReplaySession(
  uploadSessionId: string,
  expectedAttemptId?: string | null,
) {
  const response = await api.post<ApiResponse<{ uploadSessionId: string }>>(
    `/media-processing/uploads/gopro/sessions/${encodeURIComponent(uploadSessionId)}/replay`,
    {
      actionId: `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      expectedAttemptId: expectedAttemptId ?? null,
    },
  );
  return response.data.data;
}
